/*
 * Tiny event emitter for non-fatal errors, so plain modules (e.g. api.js) can trigger the
 * global error modal without React context. The GlobalErrorProvider subscribes to it.
 */

const listeners = new Set();
const pending = []; // errors notified before any listener subscribed
const MAX_PENDING = 5;

// Errors already shown/reported (by a boundary, the router or the notifier) so that the
// window "error"/"unhandledrejection" listeners don't show them a second time.
const handled = new WeakSet();

export function markErrorHandled(error) {
  if (error && (typeof error === "object" || typeof error === "function")) handled.add(error);
}

export function isErrorHandled(error) {
  return !!error && (typeof error === "object" || typeof error === "function") && handled.has(error);
}

/* API failures that deserve the global modal: server errors (5xx) and network failures (0) */
export function isUnexpectedApiError(error) {
  const status = error?.status;
  return error?.name === "ApiError" && typeof status === "number" && (status === 0 || status >= 500);
}

/* notifyGlobalError(error, { source, requestId, status, extra }) */
export function notifyGlobalError(error, meta = {}) {
  markErrorHandled(error);
  const event = { error, meta };
  if (listeners.size === 0) {
    if (pending.length < MAX_PENDING) pending.push(event);
    return;
  }
  listeners.forEach((listener) => {
    try {
      listener(event);
    } catch {
      // a broken listener must never break the caller
    }
  });
}

export function subscribeGlobalErrors(listener) {
  listeners.add(listener);
  while (pending.length) {
    const event = pending.shift();
    try {
      listener(event);
    } catch {
      // ignore
    }
  }
  return () => listeners.delete(listener);
}
